import { isE2E, isVitest } from '@/config/utils';

import { e2eAuth } from './E2EAuth';
import { mockExternalUserAuth } from './MockExternalUserAuth';
import { storebrandAuth } from './StorebrandAuth';

export interface Auth {
  initialize(): Promise<void>;
  getToken(): Promise<string>;
  getTokenParsed(): TokenParsed;
  getStaleTokenParsed(): TokenParsed | undefined;
  logout(): void;
  isAuthenticated(): boolean;
}

/**
 * The token parsed from the Keycloak token.
 *
 * Note: Add more fields as needed.
 */
export interface TokenParsed {
  /**
   * Full name of the user.
   */
  name: string;

  /**
   * The user's CMID.
   *
   * If a user is logged in via Storebrand SSO (e.g. an advisor) then the CMID will be `undefined`.
   */
  cmId: string | undefined;

  /**
   * The user's Storebrand signature.
   *
   * This will only be defined when a user is logged in via Storebrand SSO.
   * E.g. an advisor logs in to Connect.
   */
  signature: string | undefined;

  /**
   * The user's email address.
   * The field is blank for external users. Only internal users has their email exposed.
   */
  email: string | undefined;

  /**
   * The user's AMR (Authentication Method Reference).
   * This the authentication method used by the user.
   *
   * The supported AMR values can be found in the Keycloak configuration in this code base.
   *
   * @example
   * "amr": "no:bankid:regular" // The user logged in with Norwegian BankID.
   * "amr": "azure:ad:oidc" // The user logged in with SSO.
   */
  amr: string;

  /**
   * The organization the user is logged in to.
   * This is only set when the user is logged in to a specific organization.
   */
  behalfOf?: {
    cmId: string;
  };
}

export function getAuth(): Auth {
  if (isE2E()) {
    return e2eAuth;
  }

  if (isVitest()) {
    return mockExternalUserAuth;
  }

  return storebrandAuth;
}
